import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Briefcase, FileText, Receipt, LogOut, CheckCircle, Clock,
  XCircle, Download, Eye, MapPin, DollarSign, Calendar, CreditCard,
  Upload, Folder, File as FileIcon, History, ChevronRight, Building2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400' },
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400' },
  sent: { label: 'Sent', color: 'bg-blue-500/20 text-blue-400' },
  accepted: { label: 'Accepted', color: 'bg-green-500/20 text-green-400' },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400' },
  paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400' },
  overdue: { label: 'Overdue', color: 'bg-red-500/20 text-red-400' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
};

const ClientPortalDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs');
  const [selectedJob, setSelectedJob] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(null);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (token) {
      fetchClientData(token);
    } else {
      setLoading(false);
    }
  }, []);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchClientData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, jobsRes, quotesRes, invoicesRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/client-portal/me`, { headers }),
        axios.get(`${API}/api/client-portal/jobs`, { headers }),
        axios.get(`${API}/api/client-portal/quotes`, { headers }),
        axios.get(`${API}/api/client-portal/invoices`, { headers }),
        axios.get(`${API}/api/client-portal/payment-history`, { headers }).catch(() => ({ data: [] })),
      ]);
      setClient(meRes.data);
      setJobs(jobsRes.data);
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
      setPaymentHistory(historyRes.data || []);
      setIsLoggedIn(true);
    } catch (error) {
      localStorage.removeItem('client_token');
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/client-portal/login`, { email, password });
      localStorage.setItem('client_token', response.data.token);
      toast.success('Welcome back!');
      fetchClientData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/client-portal/register`, { email, password, name, phone });
      localStorage.setItem('client_token', response.data.token);
      toast.success('Account created successfully!');
      fetchClientData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    setIsLoggedIn(false);
    setClient(null);
    toast.success('Logged out');
  };

  const handleApproveQuote = async (quoteId, token) => {
    try {
      await axios.post(`${API}/api/client/quote/${token}/approve`);
      toast.success('Quote approved!');
      fetchClientData(localStorage.getItem('client_token'));
    } catch (error) {
      toast.error('Failed to approve quote');
    }
  };

  const handleDownloadPdf = async (id, token, type = 'quote') => {
    try {
      const url = type === 'quote' 
        ? `${API}/api/client/quote/${token}/pdf`
        : `${API}/api/invoices/${id}/pdf`;
      const response = await axios.get(url, { 
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('client_token')}` }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${type}-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePayInvoice = async (invoiceId, paymentMethod = 'card') => {
    setPaymentProcessing(invoiceId);
    try {
      const response = await axios.post(`${API}/api/payments/create-checkout`, {
        invoice_id: invoiceId,
        origin_url: window.location.origin,
        payment_method: paymentMethod
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('client_token')}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setPaymentProcessing(null);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out');
      return;
    }

    try {
      const response = await axios.get(`${API}/api/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Thank you.');
        // Remove session_id from URL
        navigate('/client', { replace: true });
        fetchClientData(localStorage.getItem('client_token'));
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
      } else {
        // Keep polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const fetchJobDocuments = async (jobId) => {
    try {
      const response = await axios.get(`${API}/api/client-portal/documents/${jobId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('client_token')}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments([]);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    fetchJobDocuments(job.id);
    setActiveTab('job-detail');
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedJob) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('job_id', selectedJob.id);
    formData.append('description', uploadDescription);
    
    try {
      await axios.post(`${API}/api/client-portal/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('client_token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadDescription('');
      fetchJobDocuments(selectedJob.id);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Calculate totals
  const totalOutstanding = invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + (i.total || 0), 0);
  const totalPaid = paymentHistory.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_a3b243bb-5473-4d0d-bed9-4d65df78bfbd/artifacts/t3pn1xxw_ChatGPT%20Image%20Jan%201%2C%202026%2C%2008_39_47%20PM.png" 
              alt="My Project Agent" 
              className="h-20 mx-auto mb-4"
            />
            <CardTitle className="text-2xl">{isRegistering ? 'Create Account' : 'Client Portal'}</CardTitle>
            <p className="text-muted-foreground">Access your projects, quotes, and invoices</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
              {isRegistering && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} required className="bg-background" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-background" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-background" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-background" />
              </div>
              <Button type="submit" className="w-full">{isRegistering ? 'Create Account' : 'Sign In'}</Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => setIsRegistering(!isRegistering)}>
                {isRegistering ? 'Already have an account? Sign In' : "Don't have an account? Register"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-dashboard">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_a3b243bb-5473-4d0d-bed9-4d65df78bfbd/artifacts/t3pn1xxw_ChatGPT%20Image%20Jan%201%2C%202026%2C%2008_39_47%20PM.png" 
              alt="My Project Agent" 
              className="h-10"
            />
            <div>
              <h1 className="font-semibold">Client Portal</h1>
              <p className="text-sm text-muted-foreground">Welcome, {client?.name}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout}><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <Briefcase className="h-10 w-10 text-primary opacity-50" />
              <div>
                <p className="text-2xl font-bold">{jobs.length}</p>
                <p className="text-sm text-muted-foreground">Active Projects</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <FileText className="h-10 w-10 text-blue-400 opacity-50" />
              <div>
                <p className="text-2xl font-bold">{quotes.filter(q => q.status === 'sent').length}</p>
                <p className="text-sm text-muted-foreground">Pending Quotes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <Receipt className="h-10 w-10 text-yellow-400 opacity-50" />
              <div>
                <p className="text-2xl font-bold">{formatCurrency(totalOutstanding)}</p>
                <p className="text-sm text-muted-foreground">Outstanding</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <CheckCircle className="h-10 w-10 text-green-400 opacity-50" />
              <div>
                <p className="text-2xl font-bold">{formatCurrency(totalPaid)}</p>
                <p className="text-sm text-muted-foreground">Total Paid</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-secondary/50 mb-6">
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" /> Projects
            </TabsTrigger>
            <TabsTrigger value="quotes" className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Quotes
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Invoices
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <History className="h-4 w-4" /> Payment History
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No projects found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {jobs.map(job => (
                  <Card key={job.id} className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer" onClick={() => handleJobClick(job)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            {job.title}
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.address}</span>
                            {job.start_date && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(job.start_date)}</span>}
                          </div>
                          {job.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{job.description}</p>}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[job.status]?.color}`}>
                          {statusConfig[job.status]?.label}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-4 text-sm">
                        <span className="text-muted-foreground">{job.quote_count || 0} quotes</span>
                        <span className="text-muted-foreground">{job.invoice_count || 0} invoices</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Job Detail Tab */}
          <TabsContent value="job-detail">
            {selectedJob && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <Button variant="ghost" onClick={() => setActiveTab('jobs')}>
                    ← Back to Projects
                  </Button>
                </div>
                
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{selectedJob.title}</CardTitle>
                        <p className="text-muted-foreground flex items-center gap-2 mt-1">
                          <MapPin className="h-4 w-4" /> {selectedJob.address}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[selectedJob.status]?.color}`}>
                        {statusConfig[selectedJob.status]?.label}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedJob.description && (
                      <p className="text-muted-foreground mb-4">{selectedJob.description}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Start Date</p>
                        <p className="font-medium">{formatDate(selectedJob.start_date)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">End Date</p>
                        <p className="font-medium">{formatDate(selectedJob.end_date)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Budget</p>
                        <p className="font-medium">{formatCurrency(selectedJob.budget)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Status</p>
                        <p className="font-medium capitalize">{selectedJob.status?.replace('_', ' ')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Documents Section */}
                <Card className="bg-card border-border">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Folder className="h-5 w-5" /> Documents
                    </CardTitle>
                    <Button size="sm" onClick={() => setShowUploadDialog(true)}>
                      <Upload className="h-4 w-4 mr-2" /> Upload
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {documents.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No documents uploaded yet</p>
                    ) : (
                      <div className="space-y-2">
                        {documents.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileIcon className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{doc.original_filename}</p>
                                <p className="text-xs text-muted-foreground">{formatDate(doc.created_at)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Job Quotes */}
                {selectedJob.quotes?.length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">Quotes</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedJob.quotes.map(quote => (
                        <div key={quote.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                          <div>
                            <p className="font-medium">{quote.quote_number}</p>
                            <p className="text-sm text-muted-foreground">{formatCurrency(quote.total)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${statusConfig[quote.status]?.color}`}>
                              {statusConfig[quote.status]?.label}
                            </span>
                            <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(quote.id, quote.client_token, 'quote')}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Job Invoices */}
                {selectedJob.invoices?.length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">Invoices</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedJob.invoices.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                          <div>
                            <p className="font-medium">{inv.invoice_number}</p>
                            <p className="text-sm text-muted-foreground">{formatCurrency(inv.total)} • Due: {formatDate(inv.due_date)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${statusConfig[inv.status]?.color}`}>
                              {statusConfig[inv.status]?.label}
                            </span>
                            {inv.status !== 'paid' && (
                              <Button size="sm" onClick={() => handlePayInvoice(inv.id)} disabled={paymentProcessing === inv.id}>
                                <CreditCard className="h-4 w-4 mr-1" />
                                {paymentProcessing === inv.id ? 'Processing...' : 'Pay'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            {quotes.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No quotes found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {quotes.map(quote => (
                  <Card key={quote.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{quote.quote_number}</h3>
                          <p className="text-sm text-muted-foreground">{quote.job?.title} - {quote.job?.address}</p>
                          <p className="text-lg font-bold mt-2">{formatCurrency(quote.total)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[quote.status]?.color}`}>
                            {statusConfig[quote.status]?.label}
                          </span>
                          <p className="text-xs text-muted-foreground mt-2">Valid until: {formatDate(quote.valid_until)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(quote.id, quote.client_token, 'quote')}>
                          <Download className="h-4 w-4 mr-1" /> PDF
                        </Button>
                        {quote.status === 'sent' && (
                          <Button size="sm" onClick={() => handleApproveQuote(quote.id, quote.client_token)}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            {invoices.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No invoices found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map(inv => (
                  <Card key={inv.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{inv.invoice_number}</h3>
                          <p className="text-sm text-muted-foreground">{inv.job?.title}</p>
                          <p className="text-lg font-bold mt-2">{formatCurrency(inv.total)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[inv.status]?.color}`}>
                            {statusConfig[inv.status]?.label}
                          </span>
                          <p className="text-xs text-muted-foreground mt-2">Due: {formatDate(inv.due_date)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(inv.id, null, 'invoice')}>
                          <Download className="h-4 w-4 mr-1" /> PDF
                        </Button>
                        {inv.status !== 'paid' && (
                          <>
                            <Button size="sm" onClick={() => handlePayInvoice(inv.id, 'card')} disabled={paymentProcessing === inv.id}>
                              <CreditCard className="h-4 w-4 mr-1" />
                              {paymentProcessing === inv.id ? 'Processing...' : 'Pay with Card'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handlePayInvoice(inv.id, 'bank')} disabled={paymentProcessing === inv.id}>
                              <Building2 className="h-4 w-4 mr-1" /> Bank Transfer
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Payment History Tab */}
          <TabsContent value="payments">
            {paymentHistory.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payment history</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {paymentHistory.map(payment => (
                  <Card key={payment.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">Invoice {payment.invoice_number}</h3>
                          <p className="text-sm text-muted-foreground">{formatDate(payment.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{formatCurrency(payment.amount)}</p>
                          <span className={`px-2 py-1 rounded text-xs ${payment.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {payment.payment_status === 'paid' ? 'Completed' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">File</label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description (optional)</label>
              <Textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Brief description of the document..."
                className="bg-background"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={!uploadFile || uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortalDashboard;
